/**
 * Client approvals in the client portal (MB's R191; decision 1010 item 2, 3 Nov 2026: "Approvals by the client will be
 * for survey block changes, JSA, JSP, risk assessments and for signing contracts"; docs/specs/portal/client-approvals.md).
 *
 * Staff send an item to the client for approval from where the item lives (a block on the project's Approvals tab, a
 * version on the Safety tab, a contract on Contracts). A client admin of the project's client organisation approves
 * it or declines it with a reason; a contract is signed through DocuSeal (decision 854: docuseal.com), reusing the
 * organisation's DocuSeal key and the `docuseal` function. Each request records who sent it and when, which version
 * (its SHA-256), who decided, when and why; a newer version sent for the same item supersedes a request still
 * waiting. The database (`20261103231500_client_approvals.sql`) decides who may do what; these rules are the words,
 * the block change's before and after, and the display-only role checks.
 */
import {
  blockFeatures,
  edged,
  plannedLineFeatures,
  type LayerResult,
  type MapFeature,
} from './map-layers.js';
import type { Block, PlannedLine } from './model.js';

// ------------------------------------------------------------------------------------------------ kinds and states

/** What a client approves: decision 1010 item 2. */
export const APPROVAL_KINDS = ['block_change', 'jsa', 'jsp', 'risk_assessment', 'contract'] as const;
export type ApprovalKind = (typeof APPROVAL_KINDS)[number];

export const APPROVAL_KIND_LABEL: Readonly<Record<ApprovalKind, string>> = {
  block_change: 'Survey block change',
  jsa: 'JSA',
  jsp: 'JSP',
  risk_assessment: 'Risk assessment',
  contract: 'Contract',
};

/** A request's state (`approval_request.state`). */
export const APPROVAL_STATES = ['waiting', 'approved', 'declined', 'superseded', 'withdrawn'] as const;
export type ApprovalState = (typeof APPROVAL_STATES)[number];

/** The Safety document kind an approval of a JSA, JSP or risk assessment names (`safety_document.kind`). */
export function approvalSafetyKind(kind: ApprovalKind): 'jsa' | 'jsp' | 'risk_assessment' | null {
  return kind === 'jsa' || kind === 'jsp' || kind === 'risk_assessment' ? kind : null;
}

/** The approval kind of a Safety document kind (the hazards KML is never approved). */
export function approvalKindOfSafety(kind: string): ApprovalKind | null {
  return kind === 'jsa' || kind === 'jsp' || kind === 'risk_assessment' ? kind : null;
}

/**
 * The state in words. Staff read who it waits on; the client reads what is asked of them. A contract is "Signed"
 * once DocuSeal has every signature.
 */
export function approvalStateWords(
  state: ApprovalState,
  kind: ApprovalKind,
  reader: 'staff' | 'client' = 'staff',
): string {
  switch (state) {
    case 'waiting':
      if (reader === 'client')
        return kind === 'contract' ? 'Waiting for your signature' : 'Waiting for your approval';
      return kind === 'contract' ? 'Waiting for the client to sign' : 'Waiting for the client';
    case 'approved':
      return kind === 'contract' ? 'Signed' : 'Approved';
    case 'declined':
      return 'Declined';
    case 'superseded':
      return 'Superseded by a newer version';
    case 'withdrawn':
      return 'Withdrawn';
  }
}

/** The state's tone, always shown with its words: approved pass, declined fail, waiting warn, the rest neutral. */
export function approvalTone(state: ApprovalState): 'pass' | 'warn' | 'fail' | 'neutral' {
  if (state === 'approved') return 'pass';
  if (state === 'declined') return 'fail';
  if (state === 'waiting') return 'warn';
  return 'neutral';
}

// ------------------------------------------------------------------------------------------------ who

/**
 * The client role that approves, declines and signs: the client admin (R191; decision 130 publishes the signed
 * contract to the client's admin). Client viewers see the requests of their projects (not contracts: decisions 121,
 * 130) and what was decided, and act on none. Display only: `approval_decide` and `approval_sign_begin` decide.
 */
export const CLIENT_APPROVER_ROLES: readonly string[] = ['client_admin'];

export const canApprove = (clientRole: string | null | undefined): boolean =>
  !!clientRole && CLIENT_APPROVER_ROLES.includes(clientRole);

/** Whether a client role sees a kind of request at all: contracts only to the client admin (decision 121, 130). */
export const clientSeesApproval = (clientRole: string | null | undefined, kind: ApprovalKind): boolean =>
  kind === 'contract'
    ? canApprove(clientRole)
    : clientRole === 'client_admin' || clientRole === 'client_viewer';

/**
 * Who sends each kind to the client and withdraws it (display only; `approval_send` and `approval_withdraw` decide):
 * a block change and a contract are the commercial staff's (tenant admins and managers, as quotes and contracts are
 * sent, decision 166); a JSA, JSP or risk assessment the staff who show Safety documents to the client
 * (`safety.publish`, decision 926).
 */
export const APPROVAL_SENDERS: Readonly<Record<ApprovalKind, readonly string[]>> = {
  block_change: ['tenant_admin', 'manager'],
  jsa: ['tenant_admin', 'manager', 'data_processor', 'field_crew'],
  jsp: ['tenant_admin', 'manager', 'data_processor', 'field_crew'],
  risk_assessment: ['tenant_admin', 'manager', 'data_processor', 'field_crew'],
  contract: ['tenant_admin', 'manager'],
};

export const canSendApproval = (roles: readonly string[], kind: ApprovalKind): boolean =>
  roles.some((r) => APPROVAL_SENDERS[kind].includes(r));

// ------------------------------------------------------------------------------------------------ words people type

/** The longest decline reason and staff note kept. */
export const APPROVAL_TEXT_MAX = 1000;

/** A decline needs its reason (the operator changes the item from it); null when it is fine. */
export function declineReasonProblem(reason: string): string | null {
  const r = reason.trim();
  if (!r) return 'Say why you decline, so the operator knows what to change.';
  if (r.length > APPROVAL_TEXT_MAX) return `Keep the reason to ${APPROVAL_TEXT_MAX} characters.`;
  return null;
}

/** The note staff send with a request (optional). */
export function approvalNoteProblem(note: string): string | null {
  return note.trim().length > APPROVAL_TEXT_MAX ? `Keep the note to ${APPROVAL_TEXT_MAX} characters.` : null;
}

/** The first 12 hex digits of a SHA-256, in groups of four, as the pages print a version's fingerprint. */
export function shortHash(sha256: string): string {
  const h = sha256
    .toLowerCase()
    .replace(/[^0-9a-f]/g, '')
    .slice(0, 12);
  return h.replace(/(.{4})(?=.)/g, '$1 ');
}

/** The approval request's row as staff and the client read it (no DocuSeal ids, no signing address). */
export interface ApprovalRow {
  readonly id: string;
  readonly tenant_id: string;
  readonly client_org_id: string;
  readonly project_id: string | null;
  readonly kind: ApprovalKind;
  readonly item_id: string;
  readonly title: string;
  readonly version: number;
  readonly version_hash: string;
  readonly file_name: string;
  readonly note: string;
  readonly state: ApprovalState;
  readonly sent_at: string;
  readonly sent_by_name: string;
  readonly decided_at: string | null;
  readonly decided_by_name: string;
  readonly decline_reason: string;
  readonly superseded_by: string | null;
  readonly closed_at: string | null;
  /** A contract only: DocuSeal's state of the client's signing ('' before the client starts). */
  readonly signing_state: string;
  /** A contract only (decision 1014): the operator's manager who signs second, and when each one signed. */
  readonly manager_name?: string;
  /** The client admin who signs in DocuSeal. */
  readonly signer_name?: string;
  readonly client_signed_at?: string | null;
  readonly manager_signed_at?: string | null;
  /** A declined contract: who declined in DocuSeal ('client' or 'manager'; '' otherwise). */
  readonly declined_by?: string;
}

/**
 * The state of a request in words, a contract's signing step by step (decision 1014: the client signs, then the
 * operator's manager): "Waiting for the client" (the client reads "Waiting for your signature"), "Waiting for Mia
 * Manager", "Signed by both". Every other request reads as `approvalStateWords`.
 */
export function approvalStateText(
  r: Pick<ApprovalRow, 'state' | 'kind'> &
    Partial<Pick<ApprovalRow, 'manager_name' | 'client_signed_at' | 'manager_signed_at'>>,
  reader: 'staff' | 'client' = 'staff',
): string {
  if (r.kind === 'contract' && r.manager_name) {
    if (r.state === 'waiting') {
      if (!r.client_signed_at)
        return reader === 'client' ? 'Waiting for your signature' : 'Waiting for the client';
      return `Waiting for ${r.manager_name}`;
    }
    if (r.state === 'approved') return 'Signed by both';
  }
  return approvalStateWords(r.state, r.kind, reader);
}

/** "Version 3" of a Safety document, "Revision 2" of a contract PDF, "Plan 1" of a block change. */
export function approvalVersionText(r: Pick<ApprovalRow, 'kind' | 'version'>): string {
  if (r.kind === 'contract') return `Revision ${r.version}`;
  if (r.kind === 'block_change') return `Plan ${r.version}`;
  return `Version ${r.version}`;
}

/** A date and time as the pages print a decision ("3 Nov 2026, 14:05" in the reader's time zone). */
export function approvalWhen(iso: string, timeZone?: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    ...(timeZone ? { timeZone } : {}),
  }).format(d);
}

/** What was decided, in one line: "Approved by Jane Citizen, 3 Nov 2026, 14:05." or the decline with its reason. */
export function approvalDecisionLine(r: ApprovalRow, timeZone?: string): string {
  const when = r.decided_at ? approvalWhen(r.decided_at, timeZone) : '';
  const who = r.decided_by_name ? ` by ${r.decided_by_name}` : '';
  switch (r.state) {
    case 'approved':
      if (r.kind === 'contract' && r.manager_name) {
        const mWhen = r.manager_signed_at ? `, ${approvalWhen(r.manager_signed_at, timeZone)}` : '';
        const cWhen = r.client_signed_at ? `, ${approvalWhen(r.client_signed_at, timeZone)}` : '';
        return `Signed with DocuSeal${who}${cWhen}, then by ${r.manager_name}${mWhen}.`;
      }
      return `${r.kind === 'contract' ? 'Signed with DocuSeal' : 'Approved'}${who}${when ? `, ${when}` : ''}.`;
    case 'declined':
      return `Declined${who}${when ? `, ${when}` : ''}: ${r.decline_reason}`;
    case 'superseded':
      return 'A newer version was sent in its place.';
    case 'withdrawn':
      return `Withdrawn by the operator${r.closed_at ? `, ${approvalWhen(r.closed_at, timeZone)}` : ''}.`;
    case 'waiting':
      if (r.kind === 'contract' && r.manager_name && r.client_signed_at)
        return `Signed by the client${r.signer_name ? ` (${r.signer_name})` : ''}, ${approvalWhen(r.client_signed_at, timeZone)}; ${r.manager_name} signs next for the operator.`;
      return `Sent${r.sent_by_name ? ` by ${r.sent_by_name}` : ''}, ${approvalWhen(r.sent_at, timeZone)}${
        r.kind === 'contract' && r.manager_name
          ? `; ${r.manager_name} signs for the operator after the client`
          : ''
      }.`;
  }
}

/** The requests in the order the lists show them: waiting first, then the newest sent. */
export function sortApprovals<T extends Pick<ApprovalRow, 'state' | 'sent_at'>>(rows: readonly T[]): T[] {
  return [...rows].sort((a, b) => {
    const w = Number(b.state === 'waiting') - Number(a.state === 'waiting');
    return w !== 0 ? w : b.sent_at.localeCompare(a.sent_at);
  });
}

/** The newest request of one item (the state shown beside the item where it lives), or null. */
export function latestApproval<T extends Pick<ApprovalRow, 'item_id' | 'sent_at'>>(
  rows: readonly T[],
  itemId: string,
): T | null {
  return (
    rows.filter((r) => r.item_id === itemId).sort((a, b) => b.sent_at.localeCompare(a.sent_at))[0] ?? null
  );
}

// ------------------------------------------------------------------------------------------------ block changes

/** The block's columns a block change keeps (as `approval_block_snapshot` builds them in the database). */
export interface SnapshotBlock {
  readonly id: string;
  readonly name: string;
  readonly digit: string;
  readonly epsg: number | null;
  readonly boundary: ReadonlyArray<readonly [number, number]> | null;
  readonly planned_km: number;
  readonly trav_spacing_m: number | null;
  readonly trav_direction_deg: number | null;
  readonly tie_spacing_m: number | null;
  readonly tie_direction_deg: number | null;
  readonly terrain_clearance_m: number | null;
}

/** One original planned line of the block (reflights and patches are not the plan the client approves). */
export interface SnapshotLine {
  readonly number: number;
  readonly kind: 'traverse' | 'tie';
  readonly x1: number;
  readonly y1: number;
  readonly x2: number;
  readonly y2: number;
  readonly length_m: number;
}

/** A block's boundary and line plan as sent to the client: what the client approves, hashed as it is kept. */
export interface BlockSnapshot {
  readonly block: SnapshotBlock;
  readonly project_epsg: number | null;
  readonly lines: readonly SnapshotLine[];
}

/**
 * The snapshot of a block from its rows as Teslyn Cloud holds them (the same as the database's
 * `approval_block_snapshot`): the live original lines, ordered by number then kind.
 */
export function blockSnapshot(
  block: Pick<Block, keyof SnapshotBlock>,
  plan: readonly Pick<
    PlannedLine,
    | 'block_id'
    | 'number'
    | 'kind'
    | 'x1'
    | 'y1'
    | 'x2'
    | 'y2'
    | 'length_m'
    | 'reflight_of_line_id'
    | 'deleted_at'
  >[],
  projectEpsg: number | null,
): BlockSnapshot {
  return {
    block: {
      id: block.id,
      name: block.name,
      digit: block.digit,
      epsg: block.epsg,
      boundary: block.boundary,
      planned_km: block.planned_km,
      trav_spacing_m: block.trav_spacing_m,
      trav_direction_deg: block.trav_direction_deg,
      tie_spacing_m: block.tie_spacing_m,
      tie_direction_deg: block.tie_direction_deg,
      terrain_clearance_m: block.terrain_clearance_m,
    },
    project_epsg: projectEpsg,
    lines: plan
      .filter((l) => l.block_id === block.id && l.reflight_of_line_id === null && l.deleted_at === null)
      .map((l) => ({
        number: l.number,
        kind: l.kind,
        x1: l.x1,
        y1: l.y1,
        x2: l.x2,
        y2: l.y2,
        length_m: l.length_m,
      }))
      .sort((a, b) => a.number - b.number || a.kind.localeCompare(b.kind)),
  };
}

/** The area inside a boundary in km² (shoelace over the projected metres), 0 without one. */
export function boundaryAreaKm2(boundary: ReadonlyArray<readonly [number, number]> | null): number {
  if (!boundary || boundary.length < 3) return 0;
  let twice = 0;
  for (let i = 0; i < boundary.length; i++) {
    const [x1, y1] = boundary[i]!;
    const [x2, y2] = boundary[(i + 1) % boundary.length]!;
    twice += x1 * y2 - x2 * y1;
  }
  return Math.abs(twice) / 2 / 1e6;
}

const lineKey = (l: SnapshotLine): string => `${l.kind}:${l.number}`;
/** A line counts as moved when an end moved more than this or its length changed by more (metres). */
const MOVED_M = 0.5;

const samePoints = (
  a: ReadonlyArray<readonly [number, number]> | null,
  b: ReadonlyArray<readonly [number, number]> | null,
): boolean =>
  (a?.length ?? 0) === (b?.length ?? 0) &&
  (a ?? []).every((p, i) => Math.abs(p[0] - b![i]![0]) <= 0.01 && Math.abs(p[1] - b![i]![1]) <= 0.01);

export interface BlockChangeSummary {
  /** No earlier approved version of the block: the client approves the block as it is. */
  readonly first: boolean;
  readonly boundaryChanged: boolean;
  readonly boundaryPoints: { readonly before: number; readonly after: number };
  readonly areaKm2: { readonly before: number; readonly after: number };
  readonly lines: { readonly before: number; readonly after: number };
  readonly added: number;
  readonly removed: number;
  readonly moved: number;
  /** Σ length of the original lines, km. */
  readonly km: { readonly before: number; readonly after: number };
  /** The change in sentences, for the page and the text alternative of the map. */
  readonly words: readonly string[];
}

const km1 = (m: number): number => Math.round(m / 100) / 10;
const fmt = (n: number, digits = 1): string =>
  n.toLocaleString('en-AU', { minimumFractionDigits: digits, maximumFractionDigits: digits });

/** What changed between the block the client last approved (null: never) and the one sent now. */
export function blockChangeSummary(before: BlockSnapshot | null, after: BlockSnapshot): BlockChangeSummary {
  const b = before?.lines ?? [];
  const a = after.lines;
  const was = new Map(b.map((l) => [lineKey(l), l]));
  const now = new Map(a.map((l) => [lineKey(l), l]));
  let added = 0;
  let moved = 0;
  for (const [k, l] of now) {
    const o = was.get(k);
    if (!o) added += 1;
    else if (
      Math.hypot(o.x1 - l.x1, o.y1 - l.y1) > MOVED_M ||
      Math.hypot(o.x2 - l.x2, o.y2 - l.y2) > MOVED_M ||
      Math.abs(o.length_m - l.length_m) > MOVED_M
    )
      moved += 1;
  }
  const removed = [...was.keys()].filter((k) => !now.has(k)).length;
  const kmBefore = km1(b.reduce((s, l) => s + l.length_m, 0));
  const kmAfter = km1(a.reduce((s, l) => s + l.length_m, 0));
  const areaBefore = boundaryAreaKm2(before?.block.boundary ?? null);
  const areaAfter = boundaryAreaKm2(after.block.boundary);
  const boundaryChanged = !before || !samePoints(before.block.boundary, after.block.boundary);
  const name = `Block ${after.block.name}`;
  const words: string[] = [];
  if (!before) {
    words.push(
      `${name} as sent: ${after.block.boundary?.length ?? 0} boundary points, ${fmt(areaAfter)} km², ${a.length} planned lines, ${fmt(kmAfter)} line km. No earlier version was approved.`,
    );
  } else {
    words.push(
      boundaryChanged
        ? `The boundary changed: ${before.block.boundary?.length ?? 0} points and ${fmt(areaBefore)} km² before, ${after.block.boundary?.length ?? 0} points and ${fmt(areaAfter)} km² now.`
        : 'The boundary is unchanged.',
    );
    if (added === 0 && removed === 0 && moved === 0) words.push('The line plan is unchanged.');
    else {
      const parts = [
        added ? `${added} line${added === 1 ? '' : 's'} added` : '',
        removed ? `${removed} removed` : '',
        moved ? `${moved} moved or changed length` : '',
      ].filter(Boolean);
      words.push(`The line plan changed: ${parts.join(', ')}.`);
    }
    words.push(`Line km ${fmt(kmBefore)} before, ${fmt(kmAfter)} now.`);
  }
  return {
    first: !before,
    boundaryChanged,
    boundaryPoints: { before: before?.block.boundary?.length ?? 0, after: after.block.boundary?.length ?? 0 },
    areaKm2: { before: areaBefore, after: areaAfter },
    lines: { before: b.length, after: a.length },
    added,
    removed,
    moved,
    km: { before: kmBefore, after: kmAfter },
    words,
  };
}

const COMMON = { created_at: '', updated_at: '', updated_by: null, deleted_at: null } as const;

/**
 * The map features of one side of a block change, drawn with the project map's own layers (`blockFeatures`,
 * `plannedLineFeatures`): the boundary and the planned lines. The earlier version's boundary is drawn dashed, so the
 * two maps read apart even in print.
 */
export function blockChangeFeatures(s: BlockSnapshot, side: 'before' | 'after'): LayerResult {
  const block: Block = {
    ...COMMON,
    ...s.block,
    project_id: '',
    key: s.block.digit,
    kind: 'survey',
  };
  const where = { epsg: s.project_epsg };
  const outline = blockFeatures([block], where);
  const lines = plannedLineFeatures(
    s.lines.map((l, i) => ({
      ...COMMON,
      ...l,
      id: `${side}:${i}`,
      project_id: '',
      block_id: s.block.id,
      reflight_of_line_id: null,
    })),
    [block],
    where,
  );
  const features: MapFeature[] = outline.features.map((f) =>
    side === 'before' ? { ...f, id: `before:${f.id}`, style: edged({ ...f.style, dashed: true }) } : f,
  );
  return { features: [...features, ...lines.features], notDrawn: [...outline.notDrawn, ...lines.notDrawn] };
}

// ------------------------------------------------------------------------------------------------ notices hook

/**
 * The notice kinds client approvals give the client notices (decision 1010 item 1, built beside this): when a request
 * is sent (to the client organisation's members who see it; contracts to its client admins) and when the client
 * decides (to the staff member who sent it). The database writes the matching audit rows (`approval.sent`,
 * `approval.approved`, `approval.declined`, `approval.signed`); the notices read them.
 */
export const APPROVAL_NOTICE_KINDS = ['approval_requested', 'approval_decided'] as const;
export type ApprovalNoticeKind = (typeof APPROVAL_NOTICE_KINDS)[number];

/** The notice's subject line. */
export function approvalNoticeSubject(
  kind: ApprovalNoticeKind,
  r: Pick<ApprovalRow, 'kind' | 'title' | 'state'>,
  projectNumber: string,
): string {
  const where = projectNumber ? `${projectNumber}: ` : '';
  if (kind === 'approval_requested')
    return `${where}${r.kind === 'contract' ? 'please sign' : 'please approve'} ${r.title}`;
  return `${where}${r.title} ${approvalStateWords(r.state, r.kind).toLowerCase()}`;
}
